import { useState, useEffect } from 'react';
import {
  HoldingsOneTimeCost,
  HoldingsCostCategory,
  HoldingsCompany,
  HoldingsPerson
} from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface OneTimeCostFormProps {
  cost?: HoldingsOneTimeCost | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function OneTimeCostForm({ cost, onClose, onSaved }: OneTimeCostFormProps) {
  const isEditing = !!cost;

  const [date, setDate] = useState(cost?.date?.split('T')[0] || new Date().toISOString().split('T')[0]);
  const [amountDollars, setAmountDollars] = useState(
    cost ? (cost.amountCents / 100).toString() : ''
  );
  const [category, setCategory] = useState(cost?.category || '');
  const [description, setDescription] = useState(cost?.description || '');
  const [vendorName, setVendorName] = useState(cost?.vendorName || '');
  const [companyId, setCompanyId] = useState(cost?.companyId || '');
  const [personId, setPersonId] = useState(cost?.personId || '');
  const [receiptUrl, setReceiptUrl] = useState(cost?.receiptUrl || '');
  const [notes, setNotes] = useState(cost?.notes || '');

  const [categories, setCategories] = useState<HoldingsCostCategory[]>([]);
  const [companies, setCompanies] = useState<HoldingsCompany[]>([]);
  const [people, setPeople] = useState<HoldingsPerson[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadCategories();
    loadCompanies();
    loadPeople();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/categories`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.map((c: any) => ({
          id: c.id,
          name: c.name,
          isActive: c.is_active
        })));
      }
    } catch (e) {
      console.error('Failed to load categories:', e);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/companies`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.filter((c: any) => c.status === 'active').map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status
        })));
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    }
  };

  const loadPeople = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/people`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setPeople(data.filter((p: any) => p.is_active).map((p: any) => ({
          id: p.id,
          name: p.name,
          isActive: p.is_active
        })));
      }
    } catch (e) {
      console.error('Failed to load people:', e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) {
      setError('Date is required');
      return;
    }
    if (!amountDollars || parseFloat(amountDollars) <= 0) {
      setError('Amount must be a valid positive number');
      return;
    }
    if (!category) {
      setError('Category is required');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        date,
        amountCents: Math.round(parseFloat(amountDollars) * 100),
        category,
        description: description.trim(),
        vendorName: vendorName.trim() || null,
        companyId: companyId || null,
        personId: personId || null,
        receiptUrl: receiptUrl.trim() || null,
        notes: notes.trim() || null
      };

      const url = isEditing
        ? `${API_URL}/holdings/one-time-costs/${cost.id}`
        : `${API_URL}/holdings/one-time-costs`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save cost');
      }
    } catch (e) {
      setError('Failed to save cost');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Cost' : 'Add One-Time Cost'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Date & Amount */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Date *
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <Input
                  value={amountDollars}
                  onChange={(e) => setAmountDollars(e.target.value)}
                  placeholder="100.00"
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  className="pl-7"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Description *
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What was this expense for?"
              required
            />
          </div>

          {/* Category & Vendor */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Category *
              </label>
              <Select value={category} onChange={(e) => setCategory(e.target.value)} required>
                <option value="">Select category...</option>
                {categories.filter(c => c.isActive).map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Vendor
              </label>
              <Input
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="Company or store name"
              />
            </div>
          </div>

          {/* Assign To */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Assign to Company
              </label>
              <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <option value="">None</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Assign to Person
              </label>
              <Select value={personId} onChange={(e) => setPersonId(e.target.value)}>
                <option value="">None</option>
                {people.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </div>
          </div>

          {/* Receipt URL */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Receipt URL
            </label>
            <Input
              value={receiptUrl}
              onChange={(e) => setReceiptUrl(e.target.value)}
              placeholder="https://..."
              type="url"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this expense..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Cost'} />
          </div>
        </form>
      </div>
    </div>
  );
}
